import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import LoadingScreen from "../components/loading-screen";
import useUserStore from "../store/userStore";
import { getCurrency } from "../lib/middleware";
import { ConfirmedOrder, pushPurchase, toE164 } from "../lib/gtm";

// Meta / Snap / TikTok purchase, fired only for orders not tracked before
function trackPixels(orders: ConfirmedOrder[], currency: string) {
    if (!orders.length) return;
    const details = orders.flatMap((o) => o.details);
    const value = Number(
        orders.reduce((acc, o) => acc + Number(o.total || 0), 0).toFixed(2),
    );
    const ids = details.map((d) => String(d.product?.id ?? d.id));
    const numItems = details.reduce((acc, d) => acc + Number(d.quantity), 0);

    if (typeof fbq !== "undefined") {
        fbq("track", "Purchase", {
            value,
            currency,
            content_ids: ids,
            content_type: "product",
            num_items: numItems,
            contents: details.map((d) => ({
                id: String(d.product?.id ?? d.id),
                quantity: Number(d.quantity),
                item_price: Number(d.price),
            })),
        });
    }

    if (typeof snaptr !== "undefined") {
        snaptr("trackCustom", "Purchase", {
            value,
            currency,
            item_ids: ids,
            item_names: details.map((d) => d.product?.name),
            item_prices: details.map((d) => Number(d.price)),
            quantities: details.map((d) => Number(d.quantity)),
            transaction_id: orders.map((o) => o.id).join(","),
        });
    }

    if (window.ttq) {
        window.ttq.track("Purchase", {
            value,
            currency,
            content_ids: ids,
            content_type: "product",
            contents: details.map((d) => ({
                content_id: String(d.product?.id ?? d.id),
                quantity: Number(d.quantity),
                price: Number(d.price),
            })),
        });
    }
}

export default function OrderConfirmation() {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const user = useUserStore((state) => state.user);
    const currency = getCurrency() || "AED";
    const orderIds = (searchParams.get("order_ids") || "")
        .split(",")
        .map((id) => id.trim())
        .filter((id) => /^\d+$/.test(id));

    const { data: orders = [], isFetching } = useQuery({
        queryKey: ["order-confirmation", orderIds.join(",")],
        queryFn: async () => {
            const responses = await Promise.all(
                orderIds.map((id) => axios.get(`/user/orders/${id}`)),
            );
            return responses.map((r) => r.data.data as ConfirmedOrder);
        },
        enabled: orderIds.length > 0,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (!orders.length) return;
        trackPixels(pushPurchase(orders, user, currency), currency);
    }, [orders, user, currency]);

    if (isFetching) return <LoadingScreen />;

    const total = orders.reduce((acc, o) => acc + Number(o.total || 0), 0);
    const phone = user ? toE164(user.country_code, user.mobile) : undefined;

    return (
        <div className="mx-auto flex max-w-[852px] flex-col gap-6 px-4 pb-28 pt-14">
            <Helmet>
                <title>{t("order-confirmation.title")}</title>
            </Helmet>
            <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-[32px] font-medium text-[rgba(23,23,23,1)]">
                    {t("order-confirmation.title")}
                </h1>
                <p className="text-gray-600">
                    {t("order-confirmation.thanks")}
                </p>
            </div>

            {orders.map((order) => (
                <div
                    key={order.id}
                    className="space-y-3 rounded-lg border p-4 shadow-sm"
                >
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-semibold">
                            {t("order-confirmation.order-number")} #{order.id}
                        </h2>
                        {order.payment?.status && (
                            <span className="text-gray-600">
                                {order.payment.status}
                            </span>
                        )}
                    </div>
                    {order.details.map((detail) => (
                        <div
                            key={detail.id}
                            className="flex items-center justify-between gap-4"
                        >
                            <div className="flex items-center gap-3">
                                {detail.product?.image && (
                                    <img
                                        src={detail.product.image}
                                        alt={detail.product.name}
                                        className="h-14 w-14 rounded-lg object-cover"
                                    />
                                )}
                                <span>
                                    {detail.product?.name} × {detail.quantity}
                                </span>
                            </div>
                            <span>
                                {detail.price} {currency}
                            </span>
                        </div>
                    ))}
                    {(order.date || order.time?.from) && (
                        <div className="text-gray-600">
                            {order.date && <p>{order.date}</p>}
                            {order.time?.from && (
                                <p dir="ltr" className="w-fit">
                                    {order.time.from} - {order.time.to}
                                </p>
                            )}
                        </div>
                    )}
                    {order.address?.address && (
                        <p className="text-gray-600">
                            {t("address")}: {order.address.address}
                        </p>
                    )}
                    <p className="font-semibold">
                        {t("total")}: {order.total} {currency}
                    </p>
                </div>
            ))}

            {user && (
                <div className="space-y-1 rounded-lg border p-4 shadow-sm">
                    <p>
                        {t("email")}:{" "}
                        <span data-customer-email>{user.email}</span>
                    </p>
                    <p>
                        {t("phone")}:{" "}
                        <span data-customer-phone dir="ltr">
                            {phone}
                        </span>
                    </p>
                </div>
            )}

            {orders.length > 1 && (
                <p className="text-center text-lg font-semibold">
                    {t("total")}: {total.toFixed(2)} {currency}
                </p>
            )}

            <Link
                to="/"
                className="mx-auto rounded-lg bg-primary px-8 py-3 text-white"
            >
                {t("continue-shopping")}
            </Link>
        </div>
    );
}
